export type GameMode = "equal" | "roles";
export type GameStatus = "lobby" | "playing" | "finished";
export type CardType = "move" | "shock" | "defense";

export interface Books {
  cash: number;
  savings: number;
  cpf: number;
  investments: number;
  bnpl: number;
  loan: number;
}

export interface CardDefinition {
  key: string;
  name: string;
  type: CardType;
  value: number;
  art: string;
  note: string;
}

export interface Card extends CardDefinition {
  id: string;
}

export interface Role {
  key: string;
  name: string;
  target: number;
  power: string;
  accent: string;
  start: Partial<Books>;
  drawBonus?: number;
  incomeMultiplier?: number;
  wantsMultiplier?: number;
  cpfMultiplier?: number;
  dividendMultiplier?: number;
  lifestyleCost?: number;
}

export interface PlayerState {
  id: string;
  name: string;
  avatar: string;
  isHost: boolean;
  role: Role;
  books: Books;
  buffer: number;
  hand: Card[];
}

export interface LogEntry {
  id: string;
  tone: "good" | "bad" | "info";
  text: string;
}

export interface GameState {
  status: GameStatus;
  mode: GameMode;
  players: PlayerState[];
  deck: Card[];
  discard: Card[];
  turn: number;
  actionsLeft: number;
  winnerId: string | null;
  log: LogEntry[];
}

export type GameAction =
  | { type: "start" }
  | { type: "play"; cardId: string; targetId?: string }
  | { type: "bank"; cardId: string }
  | { type: "end_turn" };

const emptyBooks = (): Books => ({ cash: 0, savings: 0, cpf: 0, investments: 0, bnpl: 0, loan: 0 });

export const AVATARS = [
  { key: "potato", name: "Potato", image: "/assets/characters/potato.png" },
  { key: "happy", name: "Sunny", image: "/assets/characters/happy.png" },
  { key: "smarty", name: "Smarty", image: "/assets/characters/smarty-pants.png" },
  { key: "raise", name: "Eager", image: "/assets/characters/raising-hand.png" },
  { key: "cross", name: "Auntie", image: "/assets/characters/cross-arms.png" },
  { key: "suit", name: "Boss", image: "/assets/characters/suit.png" }
] as const;

export const ROLES: Role[] = [
  { key: "freshgrad", name: "The Fresh Grad", target: 30, accent: "#004AAD", power: "Time on my side: draw one extra card each turn.", drawBonus: 1, start: { cash: 5 } },
  { key: "auntie", name: "Heartland Auntie", target: 50, accent: "#069898", power: "Thrifty: Wants cost 20% less.", wantsMultiplier: 0.8, start: { cash: 8, savings: 7 } },
  { key: "gig", name: "The Gig Worker", target: 40, accent: "#6D1A36", power: "Hustle: income pays 25% more.", incomeMultiplier: 1.25, start: { cash: 8 } },
  { key: "henry", name: "Corporate Rat Henry", target: 80, accent: "#475569", power: "High earner: 1.5× income, but $1k lifestyle creep each turn.", incomeMultiplier: 1.5, lifestyleCost: 1, start: { cash: 20 } },
  { key: "chongster", name: "CPF Chongster", target: 60, accent: "#991101", power: "Chiong CPF: CPF interest pays double.", cpfMultiplier: 2, start: { cpf: 12 } },
  { key: "uncle", name: "Dividend Uncle", target: 60, accent: "#0F766E", power: "AGM regular: dividends pay double.", dividendMultiplier: 2, start: { investments: 25 } }
];

const equalRole = (): Role => ({
  key: "equal",
  name: "Equal start",
  target: 50,
  accent: "#069898",
  power: "Same $10k start. First to $50k net worth.",
  start: { cash: 10 }
});

export const CARD_DEFINITIONS: Record<string, CardDefinition> = {
  payday: { key: "payday", type: "move", name: "Pay Day", value: 3, art: "/assets/cardart/cashier.png", note: "Wallet +$3k · net worth rises" },
  sideHustle: { key: "sideHustle", type: "move", name: "Side Hustle", value: 1, art: "/assets/cardart/music.png", note: "Extra income comes in" },
  dividend: { key: "dividend", type: "move", name: "Dividends", value: 1, art: "/assets/characters/suit.png", note: "Your investments pay out" },
  carousell: { key: "carousell", type: "move", name: "Carousell Sale", value: 1, art: "/assets/cardart/urtoys.png", note: "Sell your pre-loved" },
  angbao: { key: "angbao", type: "move", name: "Ang Bao", value: 1, art: "/assets/cardart/president.png", note: "A small windfall, huat!" },
  payFirst: { key: "payFirst", type: "move", name: "Pay Yourself First", value: 1, art: "/assets/cardart/topup.png", note: "Wallet → savings" },
  invest: { key: "invest", type: "move", name: "Start Investing", value: 2, art: "/assets/characters/smarty-pants.png", note: "Wallet → investments" },
  cpfSave: { key: "cpfSave", type: "move", name: "CPF Auto-Save", value: 1, art: "/assets/cardart/inject-money.png", note: "Wallet → CPF" },
  cpfGrow: { key: "cpfGrow", type: "move", name: "CPF 4% Interest", value: 1, art: "/assets/cardart/watering.png", note: "CPF and net worth rise" },
  payBills: { key: "payBills", type: "move", name: "Pay the Bills", value: 2, art: "/assets/cardart/phone-bill.png", note: "Needs reduce your wallet" },
  treat: { key: "treat", type: "move", name: "Treat Yourself", value: 1, art: "/assets/cardart/shoes.png", note: "Wants reduce your wallet" },
  bnpl: { key: "bnpl", type: "move", name: "Buy Now Pay Later", value: 1, art: "/assets/cardart/credit-note.png", note: "Get an asset and equal debt" },
  clearDebt: { key: "clearDebt", type: "move", name: "Clear the Debt", value: 1, art: "/assets/characters/happy.png", note: "Wallet and debt both fall" },
  loan: { key: "loan", type: "move", name: "Take a Loan", value: 5, art: "/assets/cardart/loan.png", note: "Cash now, owe the bank" },
  scam: { key: "scam", type: "shock", name: "Scam Call", value: 2, art: "/assets/characters/mr-not-nice.png", note: "A rival loses $2k" },
  hospital: { key: "hospital", type: "shock", name: "Kena Hospital", value: 3, art: "/assets/characters/worried.png", note: "A rival pays $3k" },
  iras: { key: "iras", type: "shock", name: "IRAS Audit", value: 1, art: "/assets/cardart/referee.png", note: "A rival pays $1k" },
  gst: { key: "gst", type: "shock", name: "GST Hike", value: 1, art: "/assets/characters/potato.png", note: "Everyone pays $1k" },
  crash: { key: "crash", type: "shock", name: "Market Crash", value: 2, art: "/assets/cardart/newspaper.png", note: "A rival's investments dip" },
  inflation: { key: "inflation", type: "shock", name: "Hawker Inflation", value: 1, art: "/assets/cardart/receipt.png", note: "A rival pays $1k more" },
  cannot: { key: "cannot", type: "defense", name: "Cannot!", value: 1, art: "/assets/cardart/cannot-finger.png", note: "Automatically blocks a shock" },
  medishield: { key: "medishield", type: "defense", name: "MediShield", value: 1, art: "/assets/characters/smarty-pants.png", note: "Automatically blocks hospital" },
  bobian: { key: "bobian", type: "defense", name: "Bo Bian", value: 1, art: "/assets/characters/worried.png", note: "Automatically halves a shock" },
  buffer: { key: "buffer", type: "defense", name: "Rainy Day Fund", value: 1, art: "/assets/cardart/inject-money.png", note: "Automatically blocks crash or inflation" }
};

const COUNTS: Record<string, number> = {
  payday: 6, sideHustle: 5, dividend: 4, carousell: 3, angbao: 3,
  payFirst: 4, invest: 4, cpfSave: 4, cpfGrow: 5, payBills: 4, treat: 4,
  bnpl: 4, clearDebt: 4, loan: 3, scam: 3, hospital: 3, iras: 3, gst: 3,
  crash: 3, inflation: 3, cannot: 6, medishield: 3, bobian: 4, buffer: 3
};

const clone = <T>(value: T): T => structuredClone(value);
const makeId = () => crypto.randomUUID();
const log = (state: GameState, text: string, tone: LogEntry["tone"] = "info") => {
  state.log.unshift({ id: makeId(), text, tone });
  state.log = state.log.slice(0, 30);
};

export const netWorth = (player: Pick<PlayerState, "books">) => {
  const b = player.books;
  return b.cash + b.savings + b.cpf + b.investments - b.bnpl - b.loan;
};

const shuffle = <T>(items: T[]) => {
  for (let i = items.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items;
};

const buildDeck = () => shuffle(Object.entries(COUNTS).flatMap(([key, count]) =>
  Array.from({ length: count }, () => ({ ...CARD_DEFINITIONS[key], id: makeId() }))
));

const draw = (state: GameState, player: PlayerState, count: number) => {
  for (let i = 0; i < count; i += 1) {
    if (!state.deck.length) state.deck = shuffle(state.discard.splice(0));
    const card = state.deck.pop();
    if (card) player.hand.push(card);
  }
};

const beginTurn = (state: GameState) => {
  const player = state.players[state.turn];
  const creep = player.role.lifestyleCost ?? 0;
  if (creep) pay(player, creep);
  draw(state, player, 2 + (player.role.drawBonus ?? 0));
  state.actionsLeft = 3;
  log(state, `${player.name} draws. Three moves on the table.`, "info");
};

const nextTurn = (state: GameState) => {
  state.turn = (state.turn + 1) % state.players.length;
  beginTurn(state);
};

const pay = (player: PlayerState, amount: number) => {
  let remaining = amount;
  const bufferUsed = Math.min(player.buffer, remaining);
  player.buffer -= bufferUsed;
  remaining -= bufferUsed;
  const cashUsed = Math.min(player.books.cash, remaining);
  player.books.cash -= cashUsed;
  remaining -= cashUsed;
  if (remaining > 0) player.books.loan += remaining;
};

const removeCard = (state: GameState, player: PlayerState, card: Card) => {
  player.hand = player.hand.filter((candidate) => candidate.id !== card.id);
  state.discard.push(card);
};

const canPlayMove = (player: PlayerState, card: Card) => {
  const b = player.books;
  if (["payFirst", "invest", "cpfSave", "payBills"].includes(card.key)) return b.cash >= card.value;
  if (card.key === "treat") return b.cash >= Math.round(card.value * (player.role.wantsMultiplier ?? 1));
  if (card.key === "clearDebt") return b.bnpl > 0 && b.cash >= Math.min(card.value, b.bnpl);
  return true;
};

const playMove = (player: PlayerState, card: Card) => {
  const b = player.books;
  const income = Math.round(card.value * (player.role.incomeMultiplier ?? 1));
  switch (card.key) {
    case "payday": case "sideHustle": case "carousell": case "angbao": b.cash += income; break;
    case "dividend": b.cash += Math.round(income * (player.role.dividendMultiplier ?? 1)); break;
    case "payFirst": b.cash -= card.value; b.savings += card.value; break;
    case "invest": b.cash -= card.value; b.investments += card.value; break;
    case "cpfSave": b.cash -= card.value; b.cpf += card.value; break;
    case "cpfGrow": b.cpf += Math.round(card.value * (player.role.cpfMultiplier ?? 1)); break;
    case "payBills": b.cash -= card.value; break;
    case "treat": b.cash -= Math.round(card.value * (player.role.wantsMultiplier ?? 1)); break;
    case "bnpl": b.investments += card.value; b.bnpl += card.value; break;
    case "clearDebt": { const amount = Math.min(card.value, b.bnpl); b.cash -= amount; b.bnpl -= amount; break; }
    case "loan": b.cash += card.value; b.loan += card.value; break;
  }
};

const defenseFor = (player: PlayerState, shockKey: string) => {
  const keys = ["cannot"];
  if (shockKey === "hospital") keys.push("medishield");
  if (["crash", "inflation"].includes(shockKey)) keys.push("buffer");
  keys.push("bobian");
  return player.hand.find((card) => keys.includes(card.key));
};

const playShock = (state: GameState, attacker: PlayerState, card: Card, target?: PlayerState) => {
  if (card.key === "gst") {
    state.players.forEach((player) => pay(player, 1));
    log(state, `${attacker.name} triggers a GST hike. Everyone pays $1k.`, "bad");
    return;
  }
  if (!target || target.id === attacker.id) throw new Error("Choose another player for this shock.");
  const defense = defenseFor(target, card.key);
  if (defense && defense.key !== "bobian") {
    removeCard(state, target, defense);
    log(state, `${target.name} blocks ${card.name} with ${defense.name}.`, "good");
    return;
  }
  const factor = defense?.key === "bobian" ? 0.5 : 1;
  if (defense) removeCard(state, target, defense);
  if (card.key === "crash") {
    const loss = Math.min(target.books.investments, Math.ceil(card.value * factor));
    target.books.investments -= loss;
    if (!loss) pay(target, Math.ceil(factor));
  } else {
    pay(target, Math.ceil(card.value * factor));
  }
  log(state, `${card.name} hits ${target.name}${defense ? ", but Bo Bian halves it" : ""}.`, "bad");
};

const checkWinner = (state: GameState) => {
  const winner = state.players.find((player) => netWorth(player) >= player.role.target);
  if (winner) {
    state.winnerId = winner.id;
    state.status = "finished";
    log(state, `${winner.name} reaches $${winner.role.target}k net worth and wins!`, "good");
  }
};

export const createLobby = (mode: GameMode, host: { id: string; name: string; avatar: string }): GameState => ({
  status: "lobby",
  mode,
  players: [{ ...host, isHost: true, role: equalRole(), books: emptyBooks(), buffer: 0, hand: [] }],
  deck: [], discard: [], turn: 0, actionsLeft: 0, winnerId: null, log: []
});

export const addPlayer = (input: GameState, player: { id: string; name: string; avatar: string }) => {
  const state = clone(input);
  if (state.status !== "lobby") throw new Error("This game has already started.");
  if (state.players.length >= 6) throw new Error("This room is full.");
  state.players.push({ ...player, isHost: false, role: equalRole(), books: emptyBooks(), buffer: 0, hand: [] });
  return state;
};

export const applyAction = (input: GameState, actorId: string, action: GameAction): GameState => {
  const state = clone(input);
  const actor = state.players.find((player) => player.id === actorId);
  if (!actor) throw new Error("Player not found.");
  if (action.type === "start") {
    if (!actor.isHost) throw new Error("Only the host can start the game.");
    if (state.status !== "lobby") throw new Error("The game has already started.");
    if (state.players.length < 2) throw new Error("At least two players are needed.");
    state.deck = buildDeck();
    state.players.forEach((player, index) => {
      player.role = state.mode === "roles" ? ROLES[index % ROLES.length] : equalRole();
      player.books = { ...emptyBooks(), ...player.role.start };
      player.buffer = 0;
      player.hand = [];
      draw(state, player, 5);
    });
    state.status = "playing";
    log(state, `Game on: ${state.players.map((player) => player.name).join(" · ")}`, "good");
    beginTurn(state);
    return state;
  }
  if (state.status !== "playing") throw new Error("The game is not in play.");
  if (state.players[state.turn]?.id !== actorId) throw new Error("Wait for your turn.");
  if (action.type === "end_turn") {
    nextTurn(state);
    return state;
  }
  if (state.actionsLeft <= 0) throw new Error("You have no moves left.");
  const card = actor.hand.find((candidate) => candidate.id === action.cardId);
  if (!card) throw new Error("That card is no longer in your hand.");
  if (action.type === "bank") {
    actor.buffer += card.value;
    removeCard(state, actor, card);
    log(state, `${actor.name} banks ${card.name} as a $${card.value}k rainy-day buffer.`, "info");
  } else if (card.type === "move") {
    if (!canPlayMove(actor, card)) throw new Error("You do not have enough cash for that move.");
    playMove(actor, card);
    removeCard(state, actor, card);
    log(state, `${actor.name} plays ${card.name}.`, "good");
  } else if (card.type === "shock") {
    const target = state.players.find((player) => player.id === action.targetId);
    playShock(state, actor, card, target);
    removeCard(state, actor, card);
  } else {
    throw new Error("Defense cards trigger automatically. Bank it if you need room.");
  }
  state.actionsLeft -= 1;
  checkWinner(state);
  if (state.status === "playing" && state.actionsLeft === 0) nextTurn(state);
  return state;
};

export interface PublicPlayer extends Omit<PlayerState, "hand"> { handCount: number; hand?: Card[] }
export interface PublicGameState extends Omit<GameState, "players" | "deck" | "discard"> {
  players: PublicPlayer[];
  deckCount: number;
  discardCount: number;
}

export const publicState = (state: GameState, viewerId: string): PublicGameState => ({
  status: state.status,
  mode: state.mode,
  players: state.players.map(({ hand, ...player }) => ({ ...player, handCount: hand.length, ...(player.id === viewerId ? { hand } : {}) })),
  deckCount: state.deck.length,
  discardCount: state.discard.length,
  turn: state.turn,
  actionsLeft: state.actionsLeft,
  winnerId: state.winnerId,
  log: state.log
});
